import { Link } from "react-router-dom";
import { ArrowRight, Phone, CheckCircle2, Award, Users, Wrench } from "lucide-react";
import { COMPANY, MAJOR_CLIENTS, CREW } from "../data/site";
import { useReveal } from "../hooks/use-reveal";
import { useSeo } from "../hooks/use-seo";
import { localBusinessJsonLd, SITE_BASE_URL } from "../lib/jsonld";
import { ContactForm } from "../components/sections/ContactForm";

const CREDENTIALS = [
    { icon: Award, title: "BPI Certified", body: "Building Performance Institute certified — the industry standard for whole-home energy diagnostics." },
    { icon: CheckCircle2, title: "NICOR & ComEd Partner", body: "Approved rebate contractor — we file the paperwork so you get the check directly." },
    { icon: Users, title: "Real Crew, Same Standard", body: "Jamie, Jose, Denni, Juan — the same people who scope your job do the install." },
    { icon: Wrench, title: "Licensed & Insured · IL & IN", body: "Active GL coverage, workers comp, and bonded crews on both sides of the state line." },
];

export default function About() {
    useReveal();
    useSeo({
        title: "About James Minsky & HAZ Insulation | Chicagoland's Insulation Specialist",
        description:
            "Meet James — founder of HAZ Insulation. 25+ years insulating Chicagoland homes, 500+ retrofits, BPI-certified, and a NICOR rebate partner.",
        canonical: `${SITE_BASE_URL}/about`,
        jsonLd: localBusinessJsonLd({ url: `${SITE_BASE_URL}/about`, withRating: true }),
    });

    return (
        <main data-testid="about-page" className="pt-24">
            <section className="relative py-24 md:py-32 bg-ink text-bone overflow-hidden">
                <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-copper/15 blur-3xl" />
                <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
                    <div className="text-xs tracking-[0.22em] uppercase font-bold text-ember mb-5 reveal">
                        <span className="h-px w-10 bg-ember inline-block align-middle mr-3" />
                        About HAZ Insulation
                    </div>
                    <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-extrabold tracking-tight leading-[0.98] max-w-4xl reveal">
                        Built by installers.<br />
                        <span className="text-ember">Run by a homeowner&apos;s advocate.</span>
                    </h1>
                    <p className="mt-8 text-lg md:text-xl text-bone/85 max-w-3xl leading-relaxed reveal">
                        HAZ Insulation brings 50 years of combined experience to every
                        home and commercial project across Chicago Southland and Northwest Indiana.
                        500+ homes retrofitted. NICOR &amp; ComEd rebate partner. The same crew on
                        every job — start to finish.
                    </p>
                </div>
            </section>

            {/* James — founder section */}
            <section id="james" className="py-20 md:py-28 bg-bone">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-start">
                        <div className="lg:col-span-5 reveal">
                            <div className="relative">
                                <img
                                    src="/james-minsky.png"
                                    alt="James Minsky — Founder of HAZ Insulation"
                                    className="w-full aspect-[4/5] object-cover"
                                />
                                <div className="absolute -bottom-6 -right-6 bg-copper text-bone p-5 max-w-[220px] hidden md:block">
                                    <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-bone/85">
                                        Founder
                                    </div>
                                    <div className="font-heading text-2xl font-extrabold leading-tight mt-1">
                                        James Minsky
                                    </div>
                                    <div className="text-xs text-bone/85 mt-2">
                                        BPI-certified · 25 years in Chicagoland
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="lg:col-span-7 reveal">
                            <div className="text-xs tracking-[0.22em] uppercase font-bold text-moss mb-5">
                                <span className="h-px w-10 bg-moss inline-block align-middle mr-3" />
                                Meet the Founder
                            </div>
                            <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight leading-[1.05]">
                                Hi, I&apos;m <span className="text-copper">James.</span>
                            </h2>
                            <div className="mt-8 space-y-5 text-ink/85 leading-relaxed text-base md:text-lg">
                                <p>
                                    I&apos;ve been insulating Chicagoland homes for 25 years.
                                    I&apos;m the guy who shows up to your free quote, the guy who
                                    designs your scope, and the guy you&apos;ll call if anything
                                    isn&apos;t right. That&apos;s how it&apos;s always worked here,
                                    and it&apos;s how it&apos;s going to keep working.
                                </p>
                                <p>
                                    Before HAZ, I helped grow Green Attic from a single truck into
                                    a fleet of 13 — so I&apos;ve seen what scaling does to install
                                    quality, and I&apos;m doing this one differently. Smaller crew,
                                    higher standard, real BPI certification on every quote, and the
                                    NICOR &amp; ComEd rebate paperwork filed for you.
                                </p>
                                <p>
                                    My latest venture before HAZ — Titanium Insulation — earned over
                                    50 five-star Google reviews in less than 18 months. We&apos;re
                                    bringing that same standard to HAZ Insulation: blower-door
                                    verified, thermal-imaged, code-compliant, and the same crew
                                    every time.
                                </p>
                            </div>

                            <div className="mt-10 flex flex-col sm:flex-row gap-4">
                                <Link
                                    to="/contact"
                                    data-testid="about-james-cta-quote"
                                    className="btn-lift inline-flex items-center justify-center gap-2 bg-copper text-bone font-semibold py-4 px-8 hover:bg-copper-hover"
                                >
                                    Get a Free Quote from James <ArrowRight size={18} />
                                </Link>
                                <a
                                    href={COMPANY.phoneHref}
                                    data-testid="about-james-cta-phone"
                                    className="btn-lift inline-flex items-center justify-center gap-2 border-2 border-ink text-ink font-semibold py-4 px-8 hover:bg-ink hover:text-bone"
                                >
                                    <Phone size={16} /> {COMPANY.phone}
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Company story */}
            <section className="py-20 md:py-28 bg-moss text-bone relative overflow-hidden">
                <div className="absolute inset-0 grain pointer-events-none" />
                <div className="max-w-5xl mx-auto px-6 md:px-12 relative">
                    <div className="text-xs tracking-[0.22em] uppercase font-bold text-ember mb-5 reveal">
                        Our Story
                    </div>
                    <h2 className="font-heading text-3xl md:text-5xl font-bold leading-[1.05] tracking-tight reveal">
                        50 years of combined experience. One Chicagoland-grown crew.
                    </h2>
                    <div className="mt-10 space-y-6 text-bone/85 leading-relaxed text-base md:text-lg reveal">
                        <p>
                            <strong className="text-bone">HAZ Insulation</strong> has been
                            bringing full-envelope insulation to homeowners and contractors across
                            Chicago Southland and Northwest Indiana for 25 years.
                        </p>
                        <p>
                            We&apos;ve retrofitted more than{" "}
                            <strong className="text-bone">500 homes</strong> — from 1920s bungalows
                            in the South Loop to mid-century ranches in Tinley Park to brand-new
                            builds in Schererville. We&apos;ve also coordinated commercial
                            insulation work with major builders and operators.
                        </p>
                        <p>
                            As an approved <strong className="text-bone">NICOR Gas</strong> and{" "}
                            <strong className="text-bone">ComEd</strong> rebate contractor, we
                            pre-qualify your home, file the paperwork, and the rebate check (up to
                            <strong className="text-ember"> {COMPANY.maxRebate}</strong>) comes
                            directly to you within 6–8 weeks.
                        </p>
                    </div>

                    <div className="mt-12 p-7 md:p-9 bg-bone text-ink reveal">
                        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-moss mb-3">
                            Trusted by
                        </div>
                        <div className="font-heading text-lg md:text-xl font-semibold leading-snug mb-5">
                            Major commercial &amp; builder clients include:
                        </div>
                        <ul className="grid grid-cols-2 gap-x-6 gap-y-3">
                            {MAJOR_CLIENTS.map((c) => (
                                <li
                                    key={c}
                                    className="font-heading font-bold text-base md:text-lg flex items-center gap-3"
                                >
                                    <span className="h-1.5 w-1.5 bg-copper rounded-full shrink-0" />
                                    {c}
                                </li>
                            ))}
                        </ul>
                        <div className="text-xs text-muted_ink mt-5">
                            — among other large-scale partners across Chicagoland.
                        </div>
                    </div>
                </div>
            </section>

            {/* Credentials */}
            <section className="py-20 md:py-28 bg-bone">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="text-xs tracking-[0.22em] uppercase font-bold text-moss mb-5 reveal">
                        Credentials
                    </div>
                    <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mb-12 reveal">
                        What you&apos;re actually hiring.
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-line border border-line">
                        {CREDENTIALS.map((c) => {
                            const Icon = c.icon;
                            return (
                                <div key={c.title} className="bg-white p-8 md:p-10 reveal">
                                    <Icon size={28} strokeWidth={1.5} className="text-copper mb-4" />
                                    <h3 className="font-heading text-xl font-bold mb-2">
                                        {c.title}
                                    </h3>
                                    <p className="text-muted_ink leading-relaxed">{c.body}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Crew */}
            <section id="crew" className="py-20 md:py-28 bg-ink text-bone">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="text-xs tracking-[0.22em] uppercase font-bold text-ember mb-5 reveal">
                        The Crew
                    </div>
                    <h2 className="font-heading text-3xl md:text-5xl font-bold leading-[1.05] tracking-tight mb-12 reveal">
                        The same people on every job.
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px bg-bone/10 border border-bone/15">
                        {CREW.map((m) => (
                            <div key={m.name} className="bg-ink p-7 reveal">
                                <div className="font-heading text-2xl font-bold mb-1 text-ember">
                                    {m.name}
                                </div>
                                <div className="text-xs uppercase tracking-wider text-bone/60 mb-3">
                                    {m.role}
                                </div>
                                <p className="text-sm text-bone/80 leading-relaxed">{m.note}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <ContactForm />
        </main>
    );
}
